import React, { useState, useEffect, useRef, useMemo } from 'react';
import { buildInboundColumns } from './inboundColumns';
import { Table, Alert } from 'antd';
import { connect } from 'react-redux';
import Button from '../../../components/Button';
import InboundSlideOut from './InboundSlideOut';
import './voiceInbound.scss';
import axios from 'axios';
import PropTypes from 'prop-types';
import VoiceSubheader from '../VoiceSubheader';
import {
  SearchFilter,
  DobFilter,
  DateRangeFilter,
  DepartmentFilter,
  VoiceStatusFilter,
  ResultDeliveryFilter,
} from '../Filters';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { hasFeatureInLocalStorage } from '../../../global/featureFlags';
import { captureAnalytics } from '../../../actions/preload';
import { SCHEDULED_VIA_MAIRA_STATUS } from '../statusFilterConstants';

const VOICE_INBOX_DEPARTMENT_FILTER_FEATURE = 'voice_inbox_department_filter';
const MAIRA_SCHEDULING_STATUS_FEATURE = 'maira_scheduling_status';
const DEFAULT_INBOUND_TIMESTAMP = { field: 'created_at', label: 'Date and Time' };

const parseMultiSelectFilter = (filterParam) => {
  if (!filterParam) return [];

  const values = Array.isArray(filterParam) ? filterParam : String(filterParam).split(',');
  return values.map((value) => value.trim()).filter(Boolean);
};

const normalizeStatusFilterValue = (value) => String(value)
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');
const parseDepartmentFilter = parseMultiSelectFilter;
const parseStatusFilter = (filterParam) => parseMultiSelectFilter(filterParam)
  .map(normalizeStatusFilterValue)
  .filter(Boolean);

// ENG-3107: the single URL/query key for the result-delivery filter. Named once
// so a rename can't half-apply across the parse, request, reconcile and strip
// sites (ESLint would not catch a missed string literal).
const RESULT_DELIVERY_PARAM = 'result_delivery';

// The server matches result-delivery states case-insensitively, so `?result_delivery=DELIVERED`
// filters the inbox for real. Normalizing here keeps the visible chips in step with
// what the server actually applied — without it the dropdown showed nothing selected
// while the list was silently filtered, with no way to clear it from the control.
const parseResultDeliveryFilter = (filterParam) => parseMultiSelectFilter(filterParam)
  .flatMap((value) => {
    const normalized = normalizeStatusFilterValue(value);
    return normalized ? [normalized] : [];
  });

// A selected value the server does not advertise is dropped. Used by both halves of
// the result-delivery reconciliation (visible chips + persisted query, and the URL
// strip in getInboundCalls) so they cannot disagree about what survives. Returns the
// input array untouched when nothing is dropped, so effects depending on it don't
// re-run on a new-but-equal array.
const advertisedValues = (selected, options) => {
  const allowed = new Set((options || []).map((option) => option.value));
  const kept = (selected || []).filter((value) => allowed.has(value));
  return kept.length === (selected || []).length ? selected : kept;
};

const VoiceInbound = ({
  reset,
  setReset,
  history,
  isAdmin,
  departments,
}) => {
  const [query, setQuery] = useState({});
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState([]);
  const [dob, setDob] = useState(null);
  const [department, setDepartment] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [statusFilterOptions, setStatusFilterOptions] = useState([]);
  const [schedulingFilterUnavailable, setSchedulingFilterUnavailable] = useState(false);
  // ENG-3107: the inbound result-delivery filter. THE invariant for everything
  // result_delivery below, stated once: the options list is server-owned and only
  // advertised when the org is entitled to the ENG-3061 outcomes AND is already on
  // the SQL read path (the Firestore listing cannot express the predicate). So an
  // empty array means "do not render this control", not "no options yet", and any
  // selected value the server does not advertise is dropped.
  const [selectedResultDelivery, setSelectedResultDelivery] = useState([]);
  const [resultDeliveryFilterOptions, setResultDeliveryFilterOptions] = useState([]);
  const [resultDeliveryEnabled, setResultDeliveryEnabled] = useState(false);
  const [inboundTimestamp, setInboundTimestamp] = useState(DEFAULT_INBOUND_TIMESTAMP);
  const [data, setData] = useState([]);

  // The feature flag owns department visibility. The dropdown also needs at
  // least one current department, while the table/slideout keep showing
  // historical tagged calls with the "Unknown department" fallback.
  const departmentUiEnabled = hasFeatureInLocalStorage(VOICE_INBOX_DEPARTMENT_FILTER_FEATURE);
  const showDepartmentDetails = departmentUiEnabled;
  const showDepartmentFilter = departmentUiEnabled
    && ((departments && departments.length) || 0) >= 1;
  // ENG-3107: the column and the filter are gated on DIFFERENT server signals,
  // because the API deliberately separates them (see
  // CallLogsController#decorate_call_logs_with_result_delivery): rows are
  // decorated with `result_delivery` on BOTH read paths, so the badge does not
  // have to wait for an org to move to the SQL listing, while only the SQL query
  // can push the filter predicate down before pagination.
  //
  // The filter follows the advertised options, while the column follows the
  // server-owned capability. This keeps the column available on the Firestore
  // read path without leaving stale UI behind when the feature is rolled back.
  const showResultDeliveryFilter = resultDeliveryFilterOptions.length > 0;
  const showResultDeliveryColumn = resultDeliveryEnabled;
  // Scheduled-via-MAIRA: the maira_scheduling_status flag owns the scheduled
  // status pill + merged Status-filter option (rollout gate; data is captured
  // either way). The server response is the authority once loaded — the options
  // list carries the synthetic value only when the org's flag is on — so a
  // stale localStorage flag can't leave pills and filters diverging for a whole
  // session mid-rollout. localStorage is only the pre-fetch default.
  const schedulingStatusFlagEnabled = hasFeatureInLocalStorage(MAIRA_SCHEDULING_STATUS_FEATURE);
  const schedulingStatusEnabled = statusFilterOptions.length
    ? statusFilterOptions.some((option) => option.value === SCHEDULED_VIA_MAIRA_STATUS)
    : schedulingStatusFlagEnabled;
  const inboundColumns = useMemo(
    () => buildInboundColumns({
      showDepartment: showDepartmentDetails,
      departments,
      schedulingStatusEnabled,
      showResultDelivery: showResultDeliveryColumn,
      timestamp: inboundTimestamp,
    }),
    [showDepartmentDetails, departments, schedulingStatusEnabled, showResultDeliveryColumn, inboundTimestamp],
  );
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [slideOutCallIndex, setSlideOutCallIndex] = useState(null);
  const [deepLinkedCall, setDeepLinkedCall] = useState(null);
  const [isNavigationLoading, setIsNavigationLoading] = useState(false);
  const subheaderRef = useRef(null);
  const hasInitialized = useRef(false);
  const navigationCancelledRef = useRef(false);
  // Whether scheduled_via_maira arrived pre-selected from the URL at mount, and
  // whether the one-shot page-load analytics has already been evaluated. Together
  // they let a bookmarked/shared scheduled filter be counted exactly once without
  // double-firing the interaction-path event in VoiceStatusFilter.
  const scheduledFilterFromUrl = useRef(false);
  const scheduledLoadAnalyticsEvaluated = useRef(false);
  // ENG-3107: distinguishes "the server has not answered yet" from "the server
  // answered with no options". Without it, the reconciliation effect below would
  // clear a URL-applied selection on the very first render, before the response
  // that would have justified keeping it.
  const hasLoadedResultDeliveryOptions = useRef(false);
  // Monotonic fetch counter; see getInboundCalls. Guards against an out-of-order
  // response writing stale state or re-pushing a stale URL.
  const resultRequestSeq = useRef(0);
  const deepLinkLoadAttemptedFor = useRef(null);

  const callLog = useMemo(() => {
    if (slideOutCallIndex === -1) return deepLinkedCall;
    if (slideOutCallIndex === null || !data || !data[slideOutCallIndex]) return null;
    return data[slideOutCallIndex];
  }, [data, deepLinkedCall, slideOutCallIndex]);

  const getTotalPages = () => {
    const { total, pageSize } = pagination;
    if (!total || !pageSize) return 1;
    return Math.ceil(total / pageSize);
  };

  // Sync slideOutCallId with URL (skip on initial mount to avoid overwriting URL)
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      return;
    }

    const currentParams = new URLSearchParams(history.location.search);

    if (slideOutCallIndex !== null && callLog) {
      const callId = currentParams.get('callId');
      if (callId !== String(callLog.id)) {
        currentParams.set('callId', callLog.id);
        history.replace(`?${currentParams.toString()}`);
      }
    } else {
      // Remove callId from URL when slideout is closed
      if (currentParams.has('callId')) {
        currentParams.delete('callId');
        history.replace(`?${currentParams.toString()}`);
      }
    }
  }, [slideOutCallIndex, callLog]);

  useEffect(() => {
    const currentParams = new URLSearchParams(history.location.search);
    const params = Object.fromEntries(currentParams.entries());

    const searchParam = params.search;
    const page = params.page;
    const per_page = params.per_page;
    const dobParam = params.dob;
    const start_date = params.start_date;
    const end_date = params.end_date;
    // When the Department feature is off, ignore any URL-supplied department_id
    // entirely — don't parse it into state and don't forward it to the API.
    // This prevents hidden filter state from a stale/bookmarked URL.
    const departmentParam = departmentUiEnabled ? params.department_id : undefined;
    const departmentFilters = departmentUiEnabled ? parseDepartmentFilter(departmentParam) : [];
    const statusParam = params.statuses;
    // scheduled_via_maira: the SERVER owns this synthetic status — it honors the
    // value when the org's flag is on and strips it from results when off — so we
    // must NOT pre-strip it here from the racing localStorage flag. At mount the
    // authoritative options haven't loaded yet, so schedulingStatusEnabled falls
    // back to localStorage; stripping on that dropped a bookmarked
    // ?statuses=scheduled_via_maira for a server-enabled org whenever localStorage
    // was stale mid-rollout. A disabled org's lingering value is reconciled away
    // against the loaded options below, so nothing hidden survives either.
    const statusFilters = parseStatusFilter(statusParam);
    // ENG-3107: normalized, but NOT validated against the options list yet — that
    // list has not loaded at mount, and the server is the authority on whether the
    // value is honoured. Anything it does not advertise is reconciled away below.
    const resultDeliveryFilters = parseResultDeliveryFilter(params[RESULT_DELIVERY_PARAM]);
    // Record whether the scheduled filter was applied from the URL (bookmark/share)
    // so the page-load analytics effect can count it once the server confirms the
    // flag is on. See the scheduled_via_maira analytics effect below.
    scheduledFilterFromUrl.current = statusFilters.includes(SCHEDULED_VIA_MAIRA_STATUS);
    // Legacy param from the retired standalone Scheduling filter: never forward
    // it (the server ignores it silently for stale clients).
    delete params.scheduling_outcomes;
    if (departmentFilters.length) {
      params.department_id = departmentFilters;
    } else {
      delete params.department_id;
    }
    if (statusFilters.length) {
      params.statuses = statusFilters;
    } else {
      delete params.statuses;
    }
    if (resultDeliveryFilters.length) {
      params[RESULT_DELIVERY_PARAM] = resultDeliveryFilters;
    } else {
      delete params[RESULT_DELIVERY_PARAM];
    }

    setSearch(searchParam || '');
    setDateRange([
      start_date ? moment(start_date, 'YYYY-MM-DD') : null,
      end_date ? moment(end_date, 'YYYY-MM-DD') : null
    ]);
    setDob(dobParam || null);
    setDepartment(departmentFilters);
    setSelectedStatuses(statusFilters);
    setSelectedResultDelivery(resultDeliveryFilters);
    setPagination(prev => ({
      ...prev,
      current: parseInt(page, 10) || 1,
      pageSize: parseInt(per_page, 10) || 10,
    }));
    getInboundCalls(params);
    setQuery(params);
  }, []);

  // scheduled_via_maira reconciliation: the server returns the synthetic option
  // only when the org's flag is on. Once the authoritative options load, drop a
  // lingering scheduled_via_maira selection (e.g. from a URL shared by an enabled
  // org) from the visible chips and the persisted query so a disabled org doesn't
  // show a phantom Status filter. The URL itself is cleaned at the source in
  // getInboundCalls (which owns the single history write). No-op for enabled orgs
  // (the option is present), when it isn't selected, and before the first response.
  useEffect(() => {
    if (!statusFilterOptions.length) return;
    const available = statusFilterOptions.some((option) => option.value === SCHEDULED_VIA_MAIRA_STATUS);
    if (available) return;
    if (!selectedStatuses.includes(SCHEDULED_VIA_MAIRA_STATUS)) return;

    const remaining = selectedStatuses.filter((value) => value !== SCHEDULED_VIA_MAIRA_STATUS);
    setSelectedStatuses(remaining);
    setQuery((prev) => {
      const nextQuery = { ...prev };
      if (remaining.length) {
        nextQuery.statuses = remaining;
      } else {
        delete nextQuery.statuses;
      }
      return nextQuery;
    });
  }, [statusFilterOptions, selectedStatuses]);

  // ENG-3107 result_delivery reconciliation: drop any selected value the server did
  // not advertise, from the visible chips and the persisted query. That covers both
  // an org receiving NO options (unentitled, or still on the Firestore read path)
  // and an entitled org carrying a value that is simply not a real state — the
  // latter used to ride along on every request forever. The URL itself is stripped
  // at the source in getInboundCalls, which owns the single history write.
  // No-op before the first response, and whenever every selection is advertised.
  useEffect(() => {
    if (!hasLoadedResultDeliveryOptions.current) return;
    if (!selectedResultDelivery.length) return;

    const kept = advertisedValues(selectedResultDelivery, resultDeliveryFilterOptions);
    if (kept === selectedResultDelivery) return;

    setSelectedResultDelivery(kept);
    setQuery((prev) => {
      const nextQuery = { ...prev };
      if (kept.length) {
        nextQuery[RESULT_DELIVERY_PARAM] = kept;
      } else {
        delete nextQuery[RESULT_DELIVERY_PARAM];
      }
      return nextQuery;
    });
  }, [resultDeliveryFilterOptions, selectedResultDelivery]);

  // scheduled_via_maira page-load analytics: the interaction event lives in
  // VoiceStatusFilter and only fires when a user picks the option from the
  // dropdown, so a bookmarked/shared ?statuses=scheduled_via_maira was never
  // counted. Evaluate the initial URL-applied state exactly once, after the
  // authoritative options load — count it only when the org's flag is genuinely on
  // (option present) so a disabled org (reconciled away above) is never counted,
  // and never double-fire with the interaction path.
  useEffect(() => {
    if (scheduledLoadAnalyticsEvaluated.current) return;
    if (!statusFilterOptions.length) return;

    scheduledLoadAnalyticsEvaluated.current = true;
    if (!scheduledFilterFromUrl.current) return;
    if (!statusFilterOptions.some((option) => option.value === SCHEDULED_VIA_MAIRA_STATUS)) return;
    if (!selectedStatuses.includes(SCHEDULED_VIA_MAIRA_STATUS)) return;

    captureAnalytics('maira_scheduling_filter_applied', {
      outcomes: ['scheduled'],
      statuses: selectedStatuses,
      count: 1,
      source: 'page_load',
    });
  }, [statusFilterOptions, selectedStatuses]);

  // Restore the slide-out from the URL after data loads. A result-delivery link
  // can point to a call outside the loaded page (or use the Firestore path,
  // where the result-delivery filter is not available), so fall back to the
  // access-controlled single-call endpoint instead of silently opening nothing.
  useEffect(() => {
    const currentParams = new URLSearchParams(history.location.search);
    const urlCallId = currentParams.get('callId');

    if (!urlCallId) {
      deepLinkLoadAttemptedFor.current = null;
      if (deepLinkedCall) setDeepLinkedCall(null);
      return;
    }

    const foundIndex = data.findIndex((call) => String(call.id) === urlCallId);
    if (foundIndex !== -1) {
      if (deepLinkedCall) setDeepLinkedCall(null);
      // slideOutCallIndex can be -1 here (the single-call fallback already opened
      // the drawer via deepLinkedCall) as well as null (nothing open yet) -- both
      // must be promoted to the real row index, or clearing deepLinkedCall above
      // leaves callLog resolving to null and the drawer closes out from under the
      // user the instant the table catches up with the deep link it raced.
      if (slideOutCallIndex !== foundIndex) setSlideOutCallIndex(foundIndex);
      return;
    }

    if (deepLinkedCall && String(deepLinkedCall.id) === urlCallId) {
      if (slideOutCallIndex === null) setSlideOutCallIndex(-1);
      return;
    }

    if (deepLinkLoadAttemptedFor.current === urlCallId) return;
    deepLinkLoadAttemptedFor.current = urlCallId;

    axios({
      method: 'GET',
      url: `/api/v1/call_logs/${urlCallId}`,
      headers: {
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
      },
    })
      .then((response) => {
        const loadedCall = response.data?.call_log;
        const currentCallId = new URLSearchParams(history.location.search).get('callId');
        if (loadedCall && currentCallId === urlCallId) {
          setInboundTimestamp(response.data?.inbound_timestamp || DEFAULT_INBOUND_TIMESTAMP);
          setDeepLinkedCall(loadedCall);
          setSlideOutCallIndex(-1);
        }
      })
      .catch(() => {});
  }, [data, deepLinkedCall, history.location.search, slideOutCallIndex]);

  useEffect(() => {
    if (reset) {
      handleClear();
      setReset(false);
    }
  }, [reset]);

  const getInboundCalls = (newParams = {}, options = {}) => {
    const { skipTableLoading = false } = options;

    // ENG-3107: claim a sequence number before the request goes out and ignore the
    // response if a newer fetch has started since. The .then ends in a history.push
    // that re-asserts `type` from the URL as it was at call time, so without this a
    // slow in-flight fetch (e.g. the slide-out's next/prev prefetch) resolving after
    // the user follows the new "View in Voice Outbound" link would push type=Inbound
    // and yank them straight back off the Outbound tab. It also stops the stale
    // response's setState calls from clobbering fresher data.
    resultRequestSeq.current += 1;
    const requestSeq = resultRequestSeq.current;
    const isStaleResponse = () => requestSeq !== resultRequestSeq.current;

    if (!skipTableLoading) {
      setLoading(true);
    }

    // Get type and callId from current URL, not from newParams
    const currentParams = new URLSearchParams(history.location.search);
    const type = currentParams.get('type');
    const callId = currentParams.get('callId');

    // Remove type and callId from newParams if they exist
    delete newParams.type;
    delete newParams.callId;

    // Filter out empty arrays and empty strings before sending to API
    const cleanedParams = Object.entries(newParams).reduce((acc, [key, value]) => {
      if (value === null || value === undefined || value === '') {
        return acc;
      }
      if (Array.isArray(value) && value.length === 0) {
        return acc;
      }
      acc[key] = value;
      return acc;
    }, {});

    return axios({
      method: 'GET',
      url: '/api/v1/call_logs',
      params: cleanedParams,
      headers: {
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
      },
    })
      .then((response) => {
        const callLogs = response.data?.call_logs || [];
        // A newer fetch is in flight: drop this response entirely rather than
        // writing its state and re-pushing its URL over the newer one.
        if (isStaleResponse()) return callLogs;
        // The result-delivery link can switch tabs without starting another inbound
        // fetch. Do not let a response begun on Inbound restore its captured tab
        // after the router has already moved the user to Outbound.
        const currentType = new URLSearchParams(history.location.search).get('type');
        if (currentType !== type) return callLogs;

        const loadedStatusOptions = response.data?.status_filter_options || [];
        setData(callLogs);
        setStatusFilterOptions(loadedStatusOptions);
        hasLoadedResultDeliveryOptions.current = true;
        const loadedResultDeliveryOptions = response.data?.result_delivery_filter_options || [];
        setResultDeliveryFilterOptions(loadedResultDeliveryOptions);
        setResultDeliveryEnabled(Boolean(response.data?.result_delivery_enabled));
        setInboundTimestamp(response.data?.inbound_timestamp || DEFAULT_INBOUND_TIMESTAMP);
        setSchedulingFilterUnavailable(Boolean(response.data?.scheduling_filter_unavailable));
        setPagination({
          current: parseInt(response.headers?.page, 10) || 1,
          pageSize: parseInt(response.headers?.['per-page'], 10) || 10,
          total: parseInt(response.headers?.total, 10) || 0,
        });

        // Build URL params, excluding empty values
        const urlParams = {
          ...newParams,
          page: response.headers?.page || 1,
          per_page: response.headers?.['per-page'] || 10,
        };

        // scheduled_via_maira: the server is authoritative — it only offers the
        // synthetic option when the org's flag is on. If it's absent, don't persist
        // the value to the URL (a no-op the server already strips), so a refresh or
        // reshare from a disabled org can't carry it back. This is the single owner
        // of the history write, so it wins over any concurrent reconciliation.
        const schedulingAvailable = loadedStatusOptions.some((opt) => opt.value === SCHEDULED_VIA_MAIRA_STATUS);
        if (!schedulingAvailable && urlParams.statuses) {
          const remainingStatuses = parseStatusFilter(urlParams.statuses)
            .filter((value) => value !== SCHEDULED_VIA_MAIRA_STATUS);
          if (remainingStatuses.length) {
            urlParams.statuses = remainingStatuses;
          } else {
            delete urlParams.statuses;
          }
        }

        // ENG-3107: same rule for result_delivery, via the same predicate the
        // reconciliation effect uses. The effect clears the selection from state, but
        // this is the single owner of the history write, so the URL has to be
        // stripped here too or a bookmarked ?result_delivery= survives in the address
        // bar of an org the server does not honour it for.
        if (urlParams[RESULT_DELIVERY_PARAM]) {
          const keptDelivery = advertisedValues(
            parseResultDeliveryFilter(urlParams[RESULT_DELIVERY_PARAM]),
            loadedResultDeliveryOptions,
          );
          if (keptDelivery.length) {
            urlParams[RESULT_DELIVERY_PARAM] = keptDelivery;
          } else {
            delete urlParams[RESULT_DELIVERY_PARAM];
          }
        }

        Object.keys(urlParams).forEach(key => {
          const value = urlParams[key];
          if (value === null || value === undefined || value === '' ||
              (Array.isArray(value) && value.length === 0)) {
            delete urlParams[key];
          }
        });

        const urlQuery = new URLSearchParams(urlParams);
        if (type) {
          urlQuery.set('type', type);
        }
        if (callId) {
          urlQuery.set('callId', callId);
        }
        history.push(`?${urlQuery.toString()}`);

        return callLogs;
      })
      .catch((error) => {
        console.error('Failed to fetch inbound calls:', error);
        throw error;
      })
      .finally(() => {
        if (!skipTableLoading) {
          setLoading(false);
        }
      });
  };

  const handleClear = () => {
    setSearch('');
    setDateRange(null);
    setDob(null);
    setDepartment([]);
    setSelectedStatuses([]);
    setSelectedResultDelivery([]);
    setSchedulingFilterUnavailable(false);
    setQuery({ page: 1, per_page: pagination.pageSize });
    setPagination(prev => ({
      ...prev,
      current: 1,
    }));
    getInboundCalls({ page: 1, per_page: pagination.pageSize });
  };

  const handleTableChange = (newPagination) => {
    getInboundCalls({ ...query, page: newPagination.current, per_page: newPagination.pageSize });
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const updateQuery = async (rawQuery = {}) => {
    let params = { ...query };
    Object.entries(rawQuery).forEach(([key, value]) => {
      if (value === null || value.length === 0) {
        delete params[key];
      } else {
        params[key] = value;
      }
    });
    params.page = 1;
    params.per_page = pagination.pageSize;
    setQuery(params);
    await getInboundCalls(params);
  };

  const handleNavigate = (direction) => {
    if (slideOutCallIndex === -1) return;
    if (slideOutCallIndex === null || !data || isNavigationLoading) return;

    const isPrevious = direction === 'previous';
    const currentPage = pagination.current;
    const totalPages = getTotalPages();

    // Try to move within current page
    const canMoveWithinPage = isPrevious
      ? slideOutCallIndex > 0
      : slideOutCallIndex < data.length - 1;

    if (canMoveWithinPage) {
      setSlideOutCallIndex(slideOutCallIndex + (isPrevious ? -1 : 1));
      return;
    }

    // Check if cross-page navigation is possible
    const canCrossPage = isPrevious
      ? currentPage > 1
      : currentPage < totalPages;

    if (canCrossPage) {
      setIsNavigationLoading(true);

      const targetPage = currentPage + (isPrevious ? -1 : 1);
      const newQuery = { ...query, page: targetPage, per_page: pagination.pageSize };
      setQuery(newQuery);

      getInboundCalls(newQuery, { skipTableLoading: true })
        .then((callLogs) => {
          if (navigationCancelledRef.current) {
            navigationCancelledRef.current = false;
            return;
          }
          if (callLogs && callLogs.length > 0) {
            // Previous: go to last record, Next: go to first record
            setSlideOutCallIndex(isPrevious ? callLogs.length - 1 : 0);
          }
        })
        .catch(() => {
          // Error already logged in getInboundCalls
        })
        .finally(() => {
          setIsNavigationLoading(false);
        });
    }
  };

  const isNavigationDisabled = (direction) => {
    if (slideOutCallIndex === -1) return true;
    if (slideOutCallIndex === null || isNavigationLoading || !data || !data.length) {
      return true;
    }

    const currentPage = pagination.current;
    const totalPages = getTotalPages();

    if (direction === 'previous') {
      return slideOutCallIndex === 0 && currentPage <= 1;
    }
    if (direction === 'next') {
      return slideOutCallIndex >= data.length - 1 && currentPage >= totalPages;
    }
    return false;
  };

  const updateOpenCallLog = (sourceCallLog, updates) => {
    if (!sourceCallLog) return;

    const sourceCallLogId = sourceCallLog.id;
    const sourceDatabaseCallLogId = sourceCallLog.database_call_log_id;

    setData((previousData) => previousData.map((row) => {
      const matchesFirestoreCall = sourceCallLogId && row.id === sourceCallLogId;
      const matchesDatabaseCall = sourceDatabaseCallLogId && row.database_call_log_id === sourceDatabaseCallLogId;

      return matchesFirestoreCall || matchesDatabaseCall ? { ...row, ...updates } : row;
    }));
  };

  return (
    <div className='voice-inbound'>
      <VoiceSubheader ref={subheaderRef}>
        <SearchFilter updateQuery={updateQuery} search={search} setSearch={setSearch} data={data} nameField="caller_name" />
        <DobFilter updateQuery={updateQuery} dob={dob} setDob={setDob} />
        <DateRangeFilter updateQuery={updateQuery} dateRange={dateRange} setDateRange={setDateRange} isInbound />
        {showDepartmentFilter && (
          <DepartmentFilter
            updateQuery={updateQuery}
            selectedDepartment={department}
            setSelectedDepartment={setDepartment}
            departments={departments}
            showNoDepartmentOption
          />
        )}
        <VoiceStatusFilter
          updateQuery={updateQuery}
          selectedStatuses={selectedStatuses}
          setSelectedStatuses={setSelectedStatuses}
          options={statusFilterOptions}
        />
        {showResultDeliveryFilter && (
          <ResultDeliveryFilter
            updateQuery={updateQuery}
            selectedStates={selectedResultDelivery}
            setSelectedStates={setSelectedResultDelivery}
            options={resultDeliveryFilterOptions}
          />
        )}
        <div className='clear-button'>
          <Button
            type='primary'
            size='xxsmall'
            label='Clear'
            onClick={handleClear}
          />
        </div>
      </VoiceSubheader>
      {schedulingFilterUnavailable && (
        <Alert
          type="warning"
          showIcon
          style={{ margin: '8px 0' }}
          message="Scheduling filter temporarily unavailable"
          description="MAIRA scheduling results can't be filtered right now while the index finishes setting up. An empty table here does not mean there are no scheduled calls — remove &quot;Scheduled via MAIRA&quot; from the Status filter to see all calls."
        />
      )}
      <Table
        columns={inboundColumns}
        dataSource={data || []}
        rowKey={(callLog) => callLog.id}
        loading={loading}
        scroll={{ x: 'max-content' }}
        sticky={{
          offsetHeader: subheaderRef.current?.clientHeight,
        }}
        rowClassName={(_, index) => (index === slideOutCallIndex ? 'call-row-active' : '')}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          pageSizeOptions: [10, 20, 50],
          showSizeChanger: true,
          showTotal: (total, range) => `Viewing ${range[0]}-${range[1]} of ${total} calls`,
          position: ['bottomCenter'],
        }}
        onChange={(pagination) => handleTableChange(pagination)}
        onRow={(_, index) => ({
          onClick: () => {
            if (isNavigationLoading) return;
            const selectedText = window.getSelection().toString();
            if (selectedText) return;
            navigationCancelledRef.current = false;
            setSlideOutCallIndex(index);
          },
        })}
      />
      <InboundSlideOut
        open={slideOutCallIndex !== null && !!callLog}
        onClose={() => {
          navigationCancelledRef.current = true;
          setDeepLinkedCall(null);
          setSlideOutCallIndex(null);
        }}
        callLog={callLog}
        setCallIndex={handleNavigate}
        disableNext={isNavigationDisabled('next')}
        disablePrevious={isNavigationDisabled('previous')}
        isNavigationLoading={isNavigationLoading}
        departments={departments}
        showDepartment={showDepartmentDetails}
        onCallLogUpdated={updateOpenCallLog}
        timestampField={inboundTimestamp.field}
      />
    </div>
  );
};

VoiceInbound.defaultProps = {
  reset: false,
  setReset: () => {},
  history: {},
  isAdmin: false,
  departments: [],
};

VoiceInbound.propTypes = {
  reset: PropTypes.bool,
  setReset: PropTypes.func,
  history: PropTypes.object,
  isAdmin: PropTypes.bool,
  departments: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    slug: PropTypes.string,
  })),
};

const mapStateToProps = (state) => ({
  isAdmin: state.auth.isAdmin,
  // ENG-2768: voiceInboxDepartments is admin-aware (admins get all org
  // depts, non-admins get their current-org memberships). Distinct from
  // state.auth.departments so the admin-broadened list doesn't leak into
  // unrelated filter UIs.
  departments: state.auth.voiceInboxDepartments,
});

export default connect(mapStateToProps)(withRouter(VoiceInbound));
export {
  parseDepartmentFilter,
  parseStatusFilter,
  normalizeStatusFilterValue,
  SCHEDULED_VIA_MAIRA_STATUS,
};
